import Image from 'next/image'
import Link from 'next/link'
import { getReadableImageUrl } from '@/lib/directus'
import { sanitizeHTML } from '@/lib/utils'

interface SecBaProps {
	heading?: {
		title?: string
	}
	text?: {
		text?: string
	}
	before_image?: {
		alt: string
		image_file: {
			image: {
				id: string
				filename_download: string
				width?: number
				height?: number
			}
		}
	}
	after_image?: {
		alt: string
		image_file: {
			image: {
				id: string
				filename_download: string
				width?: number
				height?: number
			}
		}
	}
	button: {
		href: string
		label: string
	}
	options: string[]
	id_section: string
}

export const fields = [
	// headings
	'heading.title',

	// text
	'text.text',

	// image
	'before_image.alt',
	'before_image.image_file.image.id',
	'before_image.image_file.image.filename_download',
	'before_image.image_file.image.width',
	'before_image.image_file.image.height',

	// after image
	'after_image.alt',
	'after_image.image_file.image.id',
	'after_image.image_file.image.filename_download',
	'after_image.image_file.image.width',
	'after_image.image_file.image.height',

	// button
	'button.href',
	'button.label',
	'id_section',
	'options',
] as const

export default function SecBa({ data }: { data: SecBaProps }) {
	if (!data) return null

	const isReversed = data?.options?.includes('flex-row-reverse')

	const beforeW = data?.before_image?.image_file?.image?.width ?? 1
	const beforeH = data?.before_image?.image_file?.image?.height ?? 1
	// stack on mobile if image is too wide (rendered height at 50vw < ~120px)
	const stackOnMobile = beforeW / beforeH > 1.6
	return (
		<section
			id={data.id_section || 'ba'}
			className='SecBa w-full bg-[#737a7d] py-16'
		>
			<div
				className={`container flex flex-wrap gap-2 xl:gap-12 items-center ${
					isReversed ? 'md:flex-row-reverse' : ''
				}`}
			>
				{/* Before & After Images */}
			<div className={`image-element flex gap-2 xl:gap-6 w-full md:flex-1 ${stackOnMobile ? 'flex-col md:flex-row' : ''}`}>
				{/* Before Image */}
				<div className='relative flex-1 bg-neutral-50 rounded-lg overflow-hidden'>
					<Image
						src={getReadableImageUrl(
							data?.before_image?.image_file?.image?.id || '',
							data?.before_image?.image_file?.image?.filename_download || ''
						)}
						alt={data?.before_image?.alt || ''}
						width={0}
						height={0}
						sizes={stackOnMobile ? '(max-width: 768px) 100vw, 50vw' : '(max-width: 768px) 50vw, 50vw'}
						className='w-full h-auto'
						priority
					/>
					<div className='absolute bottom-3 right-5 text-white text-base xl:text-xl uppercase'>
						Before
					</div>
				</div>

				{/* After Image */}
				<div className='relative flex-1 bg-neutral-50 rounded-lg overflow-hidden'>
					<Image
						src={getReadableImageUrl(
							data?.after_image?.image_file?.image?.id || '',
							data?.after_image?.image_file?.image?.filename_download || ''
						)}
						alt={data?.after_image?.alt || ''}
						width={0}
						height={0}
						sizes={stackOnMobile ? '(max-width: 768px) 100vw, 50vw' : '(max-width: 768px) 50vw, 50vw'}
						className='w-full h-auto'
						priority
					/>
					<div className='absolute bottom-3 right-5 text-white text-base xl:text-xl uppercase'>
						After
					</div>
				</div>
			</div>

				<div className='max-lg:mt-12 text-element flex flex-row items-center gap-6 xl:gap-12 w-full lg:w-1/2 xl:w-1/3'>
					<Image
						src='/images/hfps-brand-vertical.svg'
						alt='HFPS Brand'
						width={25}
						height={198}
						className='max-lg:w-[15px] hidden md:block'
					/>

					<div className='flex flex-col gap-6'>
						<h2 className='text-white'>{data?.heading?.title || ''}</h2>
						<div
							className='text-white grid gap-6'
							dangerouslySetInnerHTML={sanitizeHTML(data?.text?.text || '')}
						/>
						<Link
							href={data?.button?.href || '/'}
							className='btn'
							onClick={e => {
								if (data?.button?.href) {
									e.preventDefault()
									window.location.href = data?.button?.href
								}
							}}
						>{data?.button?.label || 'View Galleries'}
						</Link>
					</div>
				</div>
			</div>
		</section>
	)
}